import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { errorToast, successToast } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";

import { axiosPostData, axiosPutData } from "@shared/api/clients";
import {
  CreateManagerialUserRequest,
  CreateManagerialUserResponse,
} from "./users.types";

import { axiosDeleteData } from "@shared/api/clients";
import { User } from "../../types";

export const createUser = async ({
  name,
  email,
  role,
  phone,
  department,
  position,
}: CreateManagerialUserRequest): Promise<CreateManagerialUserResponse> => {
  const response = await axiosPostData<
    CreateManagerialUserResponse,
    CreateManagerialUserRequest
  >("v1/managerial/users", { name, email, role, phone, department, position });
  return response;
};

export const deleteUser = async (id: string): Promise<void> => {
  await axiosDeleteData<void>(`v1/managerial/users/${id}`);
};

export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
        exact: false,
      });
      successToast({
        description: translate("common.createSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
        exact: false,
      });
      successToast({
        description: translate("common.deleteSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};

export const editUser = async ({
  id,
  name,
  email,
  role,
  phone,
  department,
  position,
}: CreateManagerialUserRequest & { id: string }): Promise<User> => {
  const response = await axiosPutData<User, CreateManagerialUserRequest>(
    `v1/managerial/users/${id}`,
    { name, email, role, phone, department, position },
  );
  return response;
};

export const useEditUserMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: editUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
        exact: false,
      });
      successToast({
        description: translate("common.editSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};
